import {
  IAccountingOrganization,
  IAccountingTransactionRequest,
  IAuthLinkResponse,
  ICategory,
  IConnectionHealth,
  IClientData,
  IGlobalRulesAiBulkStatus,
  IGlobalRulesAiUndoResult,
  IGlobalData,
  IGRApplyStatus,
  IGRApplyStatusResult, IPayee,
  IPayeeNearMatchesResponse,
  IRule,
  IVendorAlias,
  IRulesRequest,
  IRulesResponse,
  ISelectBoxData,
  ITransactionsExportRequest,
  IVendor,
  TExportResponse
} from './interface';
import {forkJoin, Observable, of, switchMap} from 'rxjs';
import {environment} from '@env/environment';
import {map, tap} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {cache} from '@common/cache-provider';
import {TAccountingPlatform, TIntegration} from '@shared/interface';
import {getHttpParams} from '@shared/common-functions.util';
import {IGlobalCategory} from './global-categories/interface';
import {IBaseDataRequest, TBankAccountSource} from '@common/types';
import {IAgDataResponse} from '../../controls/ag-grid/utils';
import {
  ITxsConfrontation,
  ITxsConfrontationChild, ITxsConfrontationChildResponse,
  ITxsConfrontationResponse
} from './transactions/sync-status/interface';
import {
  IGlobalRuleMergeCandidatesReport,
  IGlobalRuleMergeReport,
  IGlobalRuleMergeRequest,
  IRuleHistory,
  IRuleHistoryResponse,
  IRuleImpactPreview
} from './rules/interface';

export class AccountingBaseService {
  // cache here because it should depend on children provider class
  connectionsCache: { [key: string]: IAuthLinkResponse } = {};
  globalVendorsCache: IVendor[];
  vendorsCache: { [key: string]: IVendor[] } = {};
  payeeCache: { [key: string]: IPayee[] } = {};

  globalCategoriesCache: ICategory[];
  categoriesCache: { [key: string]: ICategory[] } = {};

  constructor(
    private provider: TAccountingPlatform,
    private http: HttpClient,
  ) {
  }

  // region accounting client authorization
  _getCustomerAuthLink({username, source}: {username?: string; source?: string}): Observable<IAuthLinkResponse> {
    return this.http.get<IAuthLinkResponse>(`${environment.API_URL}/accounting/${this.provider}/auth/url`, getHttpParams({username, source}));
  }

  getAuthLink(username: string, source?: string): Observable<IAuthLinkResponse> {
    if (username in this.connectionsCache) {
      return of(this.connectionsCache[username]);
    }
    // ducking api
    return forkJoin([
      this._getCustomerAuthLink({username: this.provider === 'quickbooks' ? username : undefined, source}),
      this._getCustomerAuthLink({username, source})
    ]).pipe(switchMap(([{authLink, isAuthorized}, client]) => {
      this.connectionsCache[username] = {
        authLink,
        isAuthorized: client.isAuthorized,
        accountAuthorized: isAuthorized,
      };

      return of(this.connectionsCache[username]);
    }));
  }

  getOrganizations(): Observable<IAccountingOrganization[]> {
    return this.http.get<IAccountingOrganization[]>(`${environment.API_URL}/accounting/${this.provider}/organizations`);
  }

  mapOrganization(username: string, organizationId: string, organizationName: string): Observable<void> {
    return this.http.post<void>(`${environment.API_URL}/accounting/${this.provider}/clients/${username}/organization`, {
      organizationId,
      organizationName
    }).pipe(tap(() => {
      delete this.connectionsCache[username];
      cache.resetClientsByIntegration(this.provider as TIntegration);
    }));
  }

  sendCodeUsername(authToken: string, username: string, realmId: string): Observable<any> {
    return this.http.post(`${environment.API_URL}/accounting/${this.provider}/auth/complete`, {authToken, username, realmId})
    .pipe(tap(() => delete this.connectionsCache[username]));
  }

  deleteByCustomer(): Observable<any> {
    return this.http.delete(`${environment.API_URL}/accounting/${this.provider}/auth/remove`)
    .pipe(tap(() => {
      // whole connections should be reset on backend
      this.connectionsCache = {};
      cache.resetClientsByIntegration(this.provider as TIntegration);
    }));
  }

  deleteByUsername(username: string): Observable<any> {
    return this.http.delete(`${environment.API_URL}/accounting/${this.provider}/auth/remove/${username}`)
    .pipe(tap(() => {
      delete this.connectionsCache[username];
      cache.resetClientsByIntegration(this.provider as TIntegration);
    }));
  }

  // clients whose refresh token the platform itself has rejected - not the same as "disconnected"
  // above, which only means never mapped to LedgerSync at all
  getConnectionHealth(): Observable<IConnectionHealth> {
    return this.http.get<IConnectionHealth>(`${environment.API_URL}/accounting/${this.provider}/connection-health`);
  }

  // endregion

  // region accounting data
  getBankAccounts(username: string): Observable<any> {
    return this.http.get(`${environment.API_URL}/accounting/${this.provider}/bank-accounts/${username}`);
  }

  syncData(username: string): Observable<void> {
    return this.http.put<void>(environment.API_URL + `/accounting/${this.provider}/sync-data/${username}`, {});
  }

  assignBankAccount(accountId: number, bankAccount: number, bankAccountName: string, source?: TBankAccountSource) {
    return this.http.put(`${environment.API_URL}/accounts/${accountId}/accounting/${this.provider}`, {
      bankAccount,
      bankAccountName,
      source,
    });
  }

  // region vendors
  getVendors(username: string, force?: boolean): Observable<IVendor[]> {
    if (username in this.vendorsCache && !force) {
      return of(this.vendorsCache[username]);
    }
    return this.http.get<IVendor[]>(`${environment.API_URL}/accounting-categories/vendors/${username}/${this.provider}`)
    .pipe(
      tap((items) => this.vendorsCache[username] = items),
    );
  }

  saveVendor(name: string, username: string): Observable<IVendor> {
    delete this.vendorsCache[username];
    delete this.payeeCache[username];
    return this.http.post<IVendor>(
      `${environment.API_URL}/accounting-categories/vendors/${username}/${this.provider}`, {name});
  }

  deleteVendor(username: string, vendorId: number | string): Observable<void> {
    delete this.vendorsCache[username];
    delete this.payeeCache[username];
    return this.http.delete<void>(
      `${environment.API_URL}/accounting-categories/vendors/${username}/${this.provider}/${vendorId}`
    );
  }

  getGlobalVendors(force?: boolean): Observable<IVendor[]> {
    if (this.globalVendorsCache && !force) {
      return of(this.globalVendorsCache);
    }
    return this.http.get<IVendor[]>(`${environment.API_URL}/global-vendors`)
    .pipe(
      tap((items) => this.globalVendorsCache = items),
    );
  }

  deleteGlobalVendor(vendor: IVendor): Observable<IVendor> {
    delete this.globalVendorsCache;
    return this.http.delete<IVendor>(environment.API_URL + `/global-vendors/${vendor.id}`);
  }

  saveGlobalVendor(name: string): Observable<IVendor> {
    delete this.globalVendorsCache;
    return this.http.post<IVendor>(`${environment.API_URL}/global-vendors`, {name});
  }

  updateGlobalVendor(vendor: IVendor): Observable<IVendor> {
    delete this.globalVendorsCache;
    return this.http.put<IVendor>(environment.API_URL + `/global-vendors/${vendor.id}`, vendor);
  }

  addVendorAlias(name: string, globalVendorId: number): Observable<IVendorAlias> {
    return this.http.post<IVendorAlias>(environment.API_URL + `/vendor-alias`, {name, globalVendorId});
  }

  /** Recorded spellings for a firm vendor. Fetched with ids (the global vendors list returns bare strings) so each row can be deleted. */
  getVendorAliases(globalVendorId: number): Observable<IVendorAlias[]> {
    return this.http.get<IVendorAlias[]>(environment.API_URL + '/vendor-alias', getHttpParams({globalVendorId}));
  }

  deleteVendorAlias(id: number): Observable<void> {
    return this.http.delete<void>(`${environment.API_URL}/vendor-alias/${id}`);
  }

  getPayees(username: string): Observable<IPayee[]> {
    if (username in this.payeeCache) {
      return of(this.payeeCache[username]);
    }
    return this.http.get<IPayee[]>(environment.API_URL + `/accounting-categories/payees/${username}/${this.provider}`)
    .pipe(
      tap((items) => this.payeeCache[username] = items),
    );
  }

  getGlobalPayees(): Observable<IPayee[]> {
    return this.http.get<IPayee[]>(environment.API_URL + `/accounting/${this.provider}/payees/global`);
  }

  /** Existing payees whose name is close to a suggested payee, offered as an alternative to creating a new one. */
  getPayeeNearMatches(name: string, username: string): Observable<IPayeeNearMatchesResponse> {
    return this.http.get<IPayeeNearMatchesResponse>(
      `${environment.API_URL}/accounting-categories/payees/${username}/${this.provider}/near-matches`,
      getHttpParams({name})
    );
  }
  // endregion

  // region categories
  getClientCategories(username: string, force?: boolean): Observable<ICategory[]> {
    if (username in this.categoriesCache && !force) {
      return of(this.categoriesCache[username]);
    }
    return this.http.get<ICategory[]>(
      environment.API_URL + `/accounting-categories/accounts/${username}/${this.provider}`
    )
    .pipe(
      tap((items) => this.categoriesCache[username] = items),
    );
  }

  /**
   * Client's accounts for mapping onto one global category — the ones another global category already holds
   * come back disabled with a disabledReason. Not cached: other screens must not see those flags, and a save in
   * the mapping step has to show the next time the list loads.
   */
  getClientCategoriesForGlobalCategory(username: string, globalCategoryId: number): Observable<ICategory[]> {
    return this.http.get<ICategory[]>(
      environment.API_URL + `/accounting-categories/accounts/${username}/${this.provider}`,
      getHttpParams({globalCategoryId}),
    );
  }

  saveCategory(data: ICategory): Observable<ICategory> {
    delete this.categoriesCache[data.username];
    return this.http.post<ICategory>(environment.API_URL + `/accounting/${this.provider}/accounts`, data);
  }

  deleteCategory(username: string, categoryId: number | string): Observable<void> {
    delete this.categoriesCache[username];
    return this.http.delete<void>(
      `${environment.API_URL}/accounting-categories/accounts/${username}/${this.provider}/${categoryId}`
    );
  }

  getAccountTypes(): Observable<string[]> {
    return this.http.get<{ accountTypes: string[] }>(environment.API_URL + `/accounting/${this.provider}/accounts/form`)
    .pipe(map((data) => data.accountTypes));
  }

  getAccountDetails(accountType: string): Observable<string[]> {
    return this.http.get<{ accountSubTypes: string[] }>(
      environment.API_URL + `/accounting/${this.provider}/accounts/sub-types?accountType=${accountType}`)
    .pipe(map((data) => data.accountSubTypes));
  }

  getGlobalCategories(): Observable<ICategory[]> {
    if (this.globalCategoriesCache) {
      return of(this.globalCategoriesCache);
    }
    return this.http.get<ICategory[]>(`${environment.API_URL}/accounting-categories/customer/${this.provider}`)
    .pipe(
      tap((items) => this.globalCategoriesCache = items),
    );
  }

  updateGlobalCategory(data: IGlobalCategory): Observable<IGlobalCategory> {
    return this.http[data.id ? 'put' : 'post'](environment.API_URL + '/accounting-categories', data) as
      Observable<IGlobalCategory>;
  }

  /**
   * Connects global category with customer category
   * @param clientCategories
   * @param customerCategory
   */
  assign(clientCategories: number[], customerCategory: number): Observable<boolean> {
    return this.http.put<boolean>(
      environment.API_URL + '/accounting-categories/map',
      {customerCategory, clientCategories, accountingPlatform: this.provider}
    );
  }

  /**
   * Reset mapped category id
   * @param globalCategoryId
   * @param usernames
   */
  unassign(globalCategoryId: number, usernames: string[]): Observable<boolean> {
    return this.http.post(
      environment.API_URL + '/accounting-categories/unmap',
      {globalCategoryId, usernames, accountingPlatform: this.provider}
    ) as Observable<boolean>;
  }

  // endregion

  // endregion

  // region rules
  getRulesByClient(request: IRulesRequest): Observable<IRulesResponse> {
    const {username, ...rest} = request;
    return this.http.get<IRulesResponse>(
      `${environment.API_URL}/accounting/${this.provider}/transactions/rules/user/${username}`, getHttpParams(rest)
    );
  }

  getGlobalRules(request: IRulesRequest): Observable<IRulesResponse> {
    return this.http.get<IRulesResponse>(
      environment.API_URL + `/accounting/${this.provider}/transactions/rules/global`, getHttpParams(request));
  }

  getAllRules(request: IRulesRequest): Observable<IRulesResponse> {
    return this.http.get<IRulesResponse>(
      environment.API_URL + `/accounting/${this.provider}/transactions/rules/customer`, getHttpParams(request));
  }

  readRule(ruleId: number) {
    return this.http.get(environment.API_URL + `/accounting/${this.provider}/transactions/rules/${ruleId}`);
  }

  readGlobalRule(ruleId: number) {
    return this.http.get(environment.API_URL + `/accounting/${this.provider}/transactions/rules/global/${ruleId}`);
  }

  updateRule(rule: Partial<IRule>) {
    if (rule.id) {
      return this.http.put(environment.API_URL + `/accounting/${this.provider}/transactions/rules/${rule.id}`, rule);
    } else {
      return this.http.post(environment.API_URL + `/accounting/${this.provider}/transactions/rules`, rule);
    }
  }

  updateGlobalRule(rule: Partial<IRule>) {
    if (rule.id) {
      return this.http.put(environment.API_URL + `/accounting/${this.provider}/transactions/rules/global/${rule.id}`, rule);
    } else {
      return this.http.post(environment.API_URL + `/accounting/${this.provider}/transactions/rules/global`, rule);
    }
  }

  // nothing is persisted here - same body as updateRule, just answers "what would saving this do right now"
  previewRule(rule: Partial<IRule>): Observable<IRuleImpactPreview> {
    if (rule.id) {
      return this.http.post<IRuleImpactPreview>(
        environment.API_URL + `/accounting/${this.provider}/transactions/rules/${rule.id}/preview`, rule);
    } else {
      return this.http.post<IRuleImpactPreview>(
        environment.API_URL + `/accounting/${this.provider}/transactions/rules/preview`, rule);
    }
  }

  previewGlobalRule(rule: Partial<IRule>): Observable<IRuleImpactPreview> {
    if (rule.id) {
      return this.http.post<IRuleImpactPreview>(
        environment.API_URL + `/accounting/${this.provider}/transactions/rules/global/${rule.id}/preview`, rule);
    } else {
      return this.http.post<IRuleImpactPreview>(
        environment.API_URL + `/accounting/${this.provider}/transactions/rules/global/preview`, rule);
    }
  }

  suggestGlobalRuleWithAI(transactionId: string): Observable<IRule> {
    return this.http.post<IRule>(
      environment.API_URL + `/accounting/${this.provider}/transactions/${transactionId}/rules/global/suggest-ai`,
      {}
    );
  }

  deleteRule(ruleId: number) {
    return this.http.delete(environment.API_URL + `/accounting/${this.provider}/transactions/rules/${ruleId}`);
  }

  deleteBatch(request: IRulesRequest) {
    return this.http.delete(environment.API_URL + `/accounting/${this.provider}/transactions/rules`, {body: request});
  }

  deleteBatchGlobal(request: IRulesRequest) {
    return this.http.delete(environment.API_URL + `/accounting/${this.provider}/transactions/rules/global`, {body: request});
  }

  exportGlobalRules(request: IRulesRequest) {
    return this.http.post(environment.API_URL + `/accounting/${this.provider}/transactions/rules/global/export`, request, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  exportClientRules(request: IRulesRequest) {
    const {username, ...rest} = request;
    return this.http.post(environment.API_URL + `/accounting/${this.provider}/transactions/rules/user/${username}/export`, rest, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  exportAllRules(request: IRulesRequest) {
    return this.http.post(environment.API_URL + `/accounting/${this.provider}/transactions/rules/customer/export`, request, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  deleteGlobalRule(ruleId: number) {
    return this.http.delete(environment.API_URL + `/accounting/${this.provider}/transactions/rules/global/${ruleId}`);
  }

  updatePriority(ruleId: number, newPriority: number) {
    return this.http.put(
      environment.API_URL + `/accounting/${this.provider}/transactions/rules/${ruleId}/priority/${newPriority}`, {});
  }

  updateGlobalPriority(ruleId: number, newPriority: number) {
    return this.http.put(
      environment.API_URL + `/accounting/${this.provider}/transactions/rules/global/${ruleId}/priority/${newPriority}`, {});
  }

  toggleRule(ruleId: number, how: 'enable' | 'disable') {
    return this.http.post(environment.API_URL + `/accounting/${this.provider}/transactions/rules/${how}/${ruleId}`, {});
  }

  toggleGlobalRule(ruleId: number, how: 'enable' | 'disable') {
    return this.http.post(environment.API_URL + `/accounting/${this.provider}/transactions/rules/global/${how}/${ruleId}`, {});
  }

  toggleRuleBatch(request: IRulesRequest, how: 'enable' | 'disable') {
    return this.http.post(environment.API_URL + `/accounting/${this.provider}/transactions/rules/${how}`, {...request});
  }

  toggleGlobalRuleBatch(request: IRulesRequest, how: 'enable' | 'disable') {
    return this.http.post(environment.API_URL + `/accounting/${this.provider}/transactions/rules/global/${how}`, {...request});
  }

  getApplyStatus(request: IBaseDataRequest): Observable<IGRApplyStatus[]> {
    return this.http.get<IGRApplyStatusResult>(
      environment.API_URL + `/accounting/${this.provider}/global-rule-apply-requests`, getHttpParams(request)
    )
    .pipe(map((data) => data.ruleApplyRequests));
  }

  getGlobalSelectBoxData() {
    return this.http.get<ISelectBoxData>(environment.API_URL + `/accounting/${this.provider}/transactions/rules/global/form`);
  }

  getSelectBoxDataByClient(username: string) {
    return this.http.get<ISelectBoxData>(environment.API_URL + `/accounting/${this.provider}/transactions/rules/form/${username}`);
  }

  convertRule(ruleId: number) {
    return this.http.get(environment.API_URL + `/accounting/${this.provider}/transactions/rules/convert/${ruleId}`);
  }

  getHistory(ruleId: number): Observable<IRuleHistory[]> {
    return this.http.get<IRuleHistoryResponse>(environment.API_URL + `/accounting/${this.provider}/rules/${ruleId}/history`)
      .pipe(switchMap((response) => of(response.histories)));
  }

  getGlobalHistory(ruleId: number): Observable<IRuleHistory[]> {
    return this.http.get<IRuleHistoryResponse>(environment.API_URL + `/accounting/${this.provider}/global-rules/${ruleId}/history`)
      .pipe(switchMap((response) => of(response.histories)));
  }

  getGlobalRuleMergeCandidates(): Observable<IGlobalRuleMergeCandidatesReport> {
    return this.http.get<IGlobalRuleMergeCandidatesReport>(
      `${environment.API_URL}/accounting/${this.provider}/global-rules/merge-candidates`);
  }

  mergeGlobalRules(request: IGlobalRuleMergeRequest): Observable<IGlobalRuleMergeReport> {
    return this.http.post<IGlobalRuleMergeReport>(
      `${environment.API_URL}/accounting/${this.provider}/global-rules/merge`, request);
  }

  startGlobalRulesBulkAI(): Observable<IGlobalRulesAiBulkStatus> {
    return this.http.post<IGlobalRulesAiBulkStatus>(
      `${environment.API_URL}/accounting/${this.provider}/transactions/rules/global/bulk-ai`,
      {}
    );
  }

  getGlobalRulesBulkAIStatus(): Observable<IGlobalRulesAiBulkStatus> {
    return this.http.get<IGlobalRulesAiBulkStatus>(
      `${environment.API_URL}/accounting/${this.provider}/transactions/rules/global/bulk-ai/status`
    );
  }

  // safe to call more than once; a 404 means no job is registered, same as the status 404
  cancelGlobalRulesBulkAI(): Observable<IGlobalRulesAiBulkStatus> {
    return this.http.post<IGlobalRulesAiBulkStatus>(
      `${environment.API_URL}/accounting/${this.provider}/transactions/rules/global/bulk-ai/cancel`,
      {}
    );
  }

  // removes every AI-generated rule the firm holds, across all runs - not just the most recent.
  // always call with dryRun=true first and show the plans/detail before ever sending dryRun=false
  undoGlobalRulesBulkAI(dryRun: boolean): Observable<IGlobalRulesAiUndoResult> {
    return this.http.post<IGlobalRulesAiUndoResult>(
      `${environment.API_URL}/accounting/${this.provider}/transactions/rules/global/bulk-ai/undo`,
      {},
      getHttpParams({dryRun})
    );
  }
  // endregion

  syncTransactions(request: IAccountingTransactionRequest) {
    return this.http.post(environment.API_URL + '/transactions/accounting/sync', request);
  }

  resetTransactions(username: string) {
    return this.http.put(environment.API_URL + `/transactions/accounting/${this.provider}/reset-data/${username}`, {});
  }

  getGlobalDataByClientData(request: IClientData): Observable<IGlobalData> {
    return this.http.get<IGlobalData>(
      environment.API_URL + `/accounting-categories/global-data/${this.provider}`, getHttpParams(request));
  }

  getSyncStatus(request: {}): Observable<IAgDataResponse<ITxsConfrontation>> {
    return this.http.get<ITxsConfrontationResponse>(
      environment.API_URL + `/accounting/${this.provider}/transaction-sync-requests`,
      getHttpParams(request)
    )
      .pipe(
        map(
          (data) => ({
            rowData: data.transactionSyncRequests, rowCount: data.totalItems
          })
        ),
      );
  }

  getSyncStatusByParent(request: {parent: number; endRow: number;}): Observable<IAgDataResponse<ITxsConfrontationChild>> {
    return this.http.get<ITxsConfrontationChildResponse>(
      environment.API_URL + `/accounting/${this.provider}/transaction-sync-requests/${request.parent}/transactions`,
      getHttpParams(request)
    ).pipe(
      map(
        (data) => ({
          rowData: data.auditLogs,
          rowCount: request.endRow >= data.totalItems ? data.totalItems : -1,
        })
      )
    );
  }
}
